import type { NewsArticle, NewsListPage, NewsSummary } from '@sshop/domain';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

import { newsArticleDto, newsListDto } from './news.dto';

export interface NewsService {
  list(params?: { page?: number; pageSize?: number }, signal?: AbortSignal): Promise<NewsListPage>;
  get(slug: string, signal?: AbortSignal): Promise<NewsArticle>;
}

export function createLaravelNewsService(transport: Transport): NewsService {
  return {
    async list(params, signal): Promise<NewsListPage> {
      const query: Record<string, string | number | undefined> = {};
      if (params?.page !== undefined) query.page = params.page;
      if (params?.pageSize !== undefined) query.per_page = params.pageSize;
      const res = await transport.request({
        method: 'GET',
        path: '/news',
        query,
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(newsListDto, res.data, 'GET /news');
      return {
        items: parsed.data.map((n) => ({
          id: n.id,
          slug: n.slug,
          title: n.title,
          shortDescription: n.short_description,
          coverImage: n.cover_image,
          publishedAt: n.published_at,
        })),
        page: parsed.meta.current_page,
        pageSize: parsed.meta.per_page,
        totalItems: parsed.meta.total,
        totalPages: parsed.meta.last_page,
      };
    },
    async get(slug, signal): Promise<NewsArticle> {
      const res = await transport.request({
        method: 'GET',
        path: `/news/${encodeURIComponent(slug)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
      const n = parseDto(newsArticleDto, res.data, `GET /news/${slug}`);
      const summary: NewsSummary = {
        id: n.id,
        slug: n.slug,
        title: n.title,
        shortDescription: n.short_description,
        coverImage: n.cover_image,
        publishedAt: n.published_at,
      };
      return { ...summary, text: n.text, images: n.images };
    },
  };
}
